<template>
  <div class="app-shell">
    <header v-if="isAuthed" class="top-nav">
      <div class="top-nav-inner app-nav-glass">
        <nav class="nav-links nav-links-apple">
          <router-link v-for="item in navItems" :key="item.path" :to="item.path" class="nav-link">
            {{ item.label }}
          </router-link>
        </nav>

        <span class="nav-dot"></span>

        <div class="nav-actions">
          <div class="custom-dropdown-wrapper" :class="{ 'no-pointer': !navReady }" @mouseenter="openMenu" @mouseleave="closeMenu">
            <button type="button" class="user-menu-btn">
              <img v-if="currentUser.avatar" :src="currentUser.avatar" :alt="currentUser.name" class="user-avatar-mini user-avatar-image">
              <div v-else class="user-avatar-mini">{{ currentUser.initial }}</div>
              <span class="user-name-text">{{ currentUser.name }}</span>
              <span class="caret-icon" :class="{ 'caret-up': userMenuOpen }">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              </span>
            </button>
            <transition name="apple-dropdown">
              <div class="custom-dropdown-menu" v-if="userMenuOpen">
                <router-link to="/profile" class="dropdown-item" @click="userMenuOpen = false">
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  个人中心
                </router-link>
                <div class="dropdown-divider"></div>
                <button type="button" class="dropdown-item danger-item" @click="logout">
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                  退出登录
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </header>

    <main class="page-container" :class="{ 'page-container-auth': route.path === '/' }">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in" type="transition">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </router-view>
    </main>

    <footer class="site-footer" :class="{ 'site-footer-fixed': route.path === '/' }">
      <div class="site-footer-inner">
        <a
          class="site-footer-link"
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          浙ICP备2024113598号-3
        </a>
        <p class="site-footer-text">Copyright &copy; {{ copyrightYears }} pc老师和蔡老师 版权所有</p>
      </div>
    </footer>

    <nav v-if="isAuthed" class="mobile-tabs">
      <router-link v-for="item in mobileItems" :key="item.path" :to="item.path" class="mobile-tab">
        <div>{{ item.label }}</div>
      </router-link>
      <a href="javascript:void(0)" class="mobile-tab" @click="logout">
        <div>退出</div>
      </a>
    </nav>

  </div>
</template>

<script setup>
import { computed, ref, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import gsap from 'gsap'
import { clearAuth, getAuthToken, getStoredUser } from './utils/auth'

const route = useRoute()
const router = useRouter()
const userMenuOpen = ref(false)
const navReady = ref(false)

const openMenu = () => {
  if (navReady.value) userMenuOpen.value = true
}
const closeMenu = () => {
  userMenuOpen.value = false
}
const authToken = ref(getAuthToken())
const storedUser = ref(getStoredUser())

const syncAuthState = () => {
  authToken.value = getAuthToken()
  storedUser.value = getStoredUser()
}

const navItems = [
  { path: '/stories', label: '记录' },
  { path: '/memory-book', label: '足迹' },
  { path: '/together', label: '空间' }
]

const mobileItems = navItems

const isAuthed = computed(() => Boolean(authToken.value) && route.path !== '/')
const currentYear = new Date().getFullYear()
const copyrightYears = computed(() => currentYear > 2026 ? `2026年至${currentYear}年` : '2026年')

const currentUser = computed(() => {
  const fallback = { role: 'member', name: '已登录用户', roleLabel: '空间成员', avatar: '', initial: '已' }

  try {
    const user = JSON.parse(storedUser.value || 'null')
    if (!user) {
      return fallback
    }

    const roleMap = {
      admin: '我的账号',
      partner: '她的账号',
      visitor: '访客'
    }

    return {
      role: user.role || 'member',
      name: user.nickname || user.username || fallback.name,
      roleLabel: roleMap[user.role] || '空间成员',
      avatar: user.avatar || '',
      initial: (user.nickname || user.username || fallback.name).charAt(0)
    }
  } catch (error) {
    return fallback
  }
})

const logout = async () => {
  clearAuth()
  syncAuthState()
  await router.push('/')
}

watch(
  () => route.path,
  () => {
    syncAuthState()
    userMenuOpen.value = false
  },
  { immediate: true }
)

const animateNav = () => {
  navReady.value = false
  nextTick(() => {
    const nav = document.querySelector('.top-nav-inner')
    if (nav) {
      gsap.killTweensOf(nav)
      gsap.fromTo(nav, { opacity: 0, y: -20, scale: 0.92 }, {
        opacity: 1, y: 0, scale: 1,
        duration: 0.6, ease: 'back.out(1.4)',
        onComplete: () => { setTimeout(() => { navReady.value = true }, 400) }
      })
    }
  })
}

onMounted(() => {
  window.addEventListener('storage', syncAuthState)
  window.addEventListener('us-auth-changed', syncAuthState)
  if (isAuthed.value) animateNav()
})

watch(isAuthed, (val) => { if (val) animateNav() })

onBeforeUnmount(() => {
  window.removeEventListener('storage', syncAuthState)
  window.removeEventListener('us-auth-changed', syncAuthState)
})

</script>
